import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolder, faFolderPlus, faEdit, faTrash, faTimes, faCheck, faSpinner } from '@fortawesome/free-solid-svg-icons';

const FolderManagementModal = ({
  showModal,
  onClose,
  folders = [],
  currentQuestion,
  currentQuestionFolderIds = [],
  onCreateFolder,
  onUpdateFolder,
  onDeleteFolder,
  onAddQuestionToFolder,
  onRemoveQuestionFromFolder,
  themeStyles,
  isDarkMode,
  isLoadingFolders,
  folderError,
}) => {
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderDescription, setNewFolderDescription] = useState('');
  const [editingFolder, setEditingFolder] = useState(null); // { id, name, description }
  const [folderToDelete, setFolderToDelete] = useState(null); // folderId
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!showModal) {
      setNewFolderName('');
      setNewFolderDescription('');
      setEditingFolder(null);
      setFolderToDelete(null);
      setIsSubmitting(false);
    }
  }, [showModal]);

  const handleCreate = async () => {
    if (!newFolderName.trim()) return;
    setIsSubmitting(true);
    // console.log('[FolderManagementModal] handleCreate called with:', { Name: newFolderName, Description: newFolderDescription });
    try {
      const createdFolder = await onCreateFolder({ Name: newFolderName, Description: newFolderDescription });
      // console.log('[FolderManagementModal] onCreateFolder result:', createdFolder);
      if (createdFolder) {
        setNewFolderName('');
        setNewFolderDescription('');
        // Optionally show a success message (e.g., toast)
      }
    } catch (error) {
      console.error('[FolderManagementModal] Folder creation failed in modal handler:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingFolder || !editingFolder.name.trim()) return;
    setIsSubmitting(true);
    // Use uppercase Name and Description as per api.md
    await onUpdateFolder(editingFolder.id, { Name: editingFolder.name, Description: editingFolder.description });
    setEditingFolder(null);
    setIsSubmitting(false);
  };

  const handleDelete = async () => {
    if (!folderToDelete) return;
    setIsSubmitting(true);
    await onDeleteFolder(folderToDelete);
    setFolderToDelete(null);
    setIsSubmitting(false);
  };

  const handleToggleQuestionInFolder = async (folderId) => {
    if (!currentQuestion || !currentQuestion.id) return;
    setIsSubmitting(true);
    if (currentQuestionFolderIds.includes(folderId)) {
      await onRemoveQuestionFromFolder(folderId, currentQuestion.id);
    } else {
      await onAddQuestionToFolder(folderId, currentQuestion.id);
    }
    setIsSubmitting(false);
  };

  if (!showModal) return null;

  const modalContentStyle = {
    backgroundColor: themeStyles?.modal?.content?.background || (isDarkMode ? '#1e293b' : '#ffffff'),
    color: themeStyles?.modal?.content?.color || (isDarkMode ? '#e2e8f0' : '#334155'),
    borderRadius: themeStyles?.modal?.content?.borderRadius || '12px',
    padding: '25px',
    width: '90%',
    maxWidth: '550px',
    maxHeight: '85vh',
    overflowY: 'auto',
    boxSizing: 'border-box',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
    position: 'relative',
  };

  const inputStyle = {
    width: '100%',
    padding: '10px',
    marginBottom: '10px',
    borderRadius: '6px',
    background: themeStyles?.input?.background || (isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#f1f5f9'),
    color: themeStyles?.input?.color || (isDarkMode ? '#ffffff' : '#334155'),
    border: themeStyles?.input?.border || (isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid #cbd5e1'),
    boxSizing: 'border-box',
  };

  const buttonStyle = (primary = true) => ({
    padding: '10px 15px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '500',
    backgroundColor: primary ? (themeStyles?.button?.primary?.background || (isDarkMode ? '#3b82f6' : '#2563eb'))
                             : (themeStyles?.button?.secondary?.background || (isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0')),
    color: primary ? (themeStyles?.button?.primary?.text || '#ffffff')
                   : (themeStyles?.button?.secondary?.text || (isDarkMode ? '#e2e8f0' : '#334155')),
  });

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
        backgroundColor: themeStyles?.modal?.overlay?.background || 'rgba(0, 0, 0, 0.6)',
        display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1050,
      }}
      onClick={onClose}
    >
      <div style={modalContentStyle} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} style={{ position: 'absolute', top: '15px', right: '15px', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: themeStyles?.text?.secondary }}>
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <h2 style={{ marginTop: 0, marginBottom: '20px', textAlign: 'center' }}>Manage Folders</h2>

        {/* Create New Folder / Edit Folder Form */}
        <div style={{ marginBottom: '20px', padding: '15px', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`, borderRadius: '8px' }}>
          <h3 style={{ marginTop: 0, marginBottom: '10px' }}>{editingFolder ? 'Edit Folder' : 'Create New Folder'}</h3>
          <input
            type="text"
            placeholder="Folder Name"
            value={editingFolder ? editingFolder.name : newFolderName}
            onChange={e => editingFolder ? setEditingFolder({...editingFolder, name: e.target.value}) : setNewFolderName(e.target.value)}
            style={inputStyle}
          />
          <textarea
            placeholder="Description (Optional)"
            value={editingFolder ? editingFolder.description : newFolderDescription}
            onChange={e => editingFolder ? setEditingFolder({...editingFolder, description: e.target.value}) : setNewFolderDescription(e.target.value)}
            style={{...inputStyle, minHeight: '60px', resize: 'vertical'}}
          />
          <div style={{display: 'flex', gap: '10px'}}>
            <button onClick={editingFolder ? handleUpdate : handleCreate} style={buttonStyle()} disabled={isSubmitting || (editingFolder ? !editingFolder.name.trim() : !newFolderName.trim())}>
              {isSubmitting && (editingFolder ? editingFolder.id === (editingFolder?.id) : !editingFolder) ? <FontAwesomeIcon icon={faSpinner} spin /> : (editingFolder ? 'Save Changes' : 'Create Folder')}
            </button>
            {editingFolder && (
              <button onClick={() => setEditingFolder(null)} style={buttonStyle(false)} disabled={isSubmitting}>Cancel Edit</button>
            )}
          </div>
        </div>

        {/* Folders List */}
        <h3 style={{ marginTop: '25px', marginBottom: '10px' }}>Your Folders</h3>
        {isLoadingFolders && <div style={{textAlign: 'center', padding: '10px'}}><FontAwesomeIcon icon={faSpinner} spin /> Loading folders...</div>}
        {folderError && <div style={{color: 'red', textAlign: 'center', padding: '10px'}}>{folderError}</div>}
        
        {!isLoadingFolders && !folderError && folders.length === 0 && (
          <p style={{textAlign: 'center', color: themeStyles?.text?.secondary}}>You don't have any folders yet. Create one above!</p>
        )}

        {!isLoadingFolders && !folderError && folders.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: '250px', overflowY: 'auto' }}>
            {folders.map(folder => (
              <li key={folder.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 10px',
                borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8f9fa'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <FontAwesomeIcon icon={faFolder} style={{ color: themeStyles?.text?.secondary, fontSize: '1.1em' }} />
                  <div>
                    <span style={{ fontWeight: '500' }}>{folder.name}</span>
                    {folder.description && <p style={{fontSize: '0.85em', color: themeStyles?.text?.secondary, margin: '2px 0 0 0'}}>{folder.description}</p>}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  {currentQuestion && currentQuestion.id && (
                    <button
                      onClick={() => handleToggleQuestionInFolder(folder.id)}
                      style={{
                        ...buttonStyle(currentQuestionFolderIds.includes(folder.id)),
                        padding: '6px 10px',
                        fontSize: '0.9em',
                        backgroundColor: currentQuestionFolderIds.includes(folder.id) 
                                         ? (isDarkMode ? '#d32f2f' : '#ef4444') // Red if in folder
                                         : (isDarkMode ? '#10b981' : '#10b981'), // Green if not in folder
                        color: '#ffffff'
                      }}
                      disabled={isSubmitting}
                      title={currentQuestionFolderIds.includes(folder.id) ? `Remove question from "${folder.name}"` : `Add question to "${folder.name}"`}
                    >
                      {isSubmitting && currentQuestionFolderIds.includes(folder.id) === (currentQuestionFolderIds.includes(folder.id)) ? <FontAwesomeIcon icon={faSpinner} spin size="xs"/> : (currentQuestionFolderIds.includes(folder.id) ? 'Remove' : 'Add')}
                    </button>
                  )}
                  <button onClick={() => setEditingFolder({...folder})} style={{...buttonStyle(false), padding: '6px', lineHeight: '1'}} title="Edit folder">
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button onClick={() => setFolderToDelete(folder.id)} style={{...buttonStyle(false), padding: '6px', lineHeight: '1', color: isDarkMode ? '#fca5a5' : '#ef4444'}} title="Delete folder">
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Delete Confirmation */}
        {folderToDelete && (
          <div style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            padding: '20px', background: modalContentStyle.backgroundColor, borderRadius: '8px',
            boxShadow: '0 5px 15px rgba(0,0,0,0.3)', zIndex: 1060, textAlign: 'center',
            border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.2)' : '#cbd5e1'}`
          }}>
            <p>Are you sure you want to delete this folder? This action cannot be undone.</p>
            <div style={{display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '15px'}}>
              <button onClick={handleDelete} style={{...buttonStyle(), backgroundColor: isDarkMode ? '#c53030' : '#e53e3e' }} disabled={isSubmitting}>
                {isSubmitting ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Delete'}
              </button>
              <button onClick={() => setFolderToDelete(null)} style={buttonStyle(false)} disabled={isSubmitting}>Cancel</button>
            </div>
          </div>
        )}

        <div style={{ marginTop: '25px', textAlign: 'right' }}>
          <button onClick={onClose} style={buttonStyle(false)} disabled={isSubmitting}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default FolderManagementModal;


/*
Example Usage in Parent:

const [showFolderModal, setShowFolderModal] = useState(false);
const [userFolders, setUserFolders] = useState([]); // Fetched from API
const [currentQuestionFolderIds, setCurrentQuestionFolderIds] = useState([]); // IDs of folders current question is in

const handleCreateFolder = async (folderData) => {
  // Call API, then refresh userFolders
};

const handleUpdateFolder = async (folderId, folderData) => {
  // Call API, then refresh userFolders
};

const handleDeleteFolder = async (folderId) => {
  // Call API, then refresh userFolders
};

const handleAddQuestionToFolder = async (folderId, questionId) => {
  // Call API, then refresh currentQuestionFolderIds
};

const handleRemoveQuestionFromFolder = async (folderId, questionId) => {
  // Call API, then refresh currentQuestionFolderIds
};

<FolderManagementModal
  showModal={showFolderModal}
  onClose={() => setShowFolderModal(false)}
  folders={userFolders}
  currentQuestion={currentQuestion}
  currentQuestionFolderIds={currentQuestionFolderIds}
  onCreateFolder={handleCreateFolder}
  onUpdateFolder={handleUpdateFolder}
  onDeleteFolder={handleDeleteFolder}
  onAddQuestionToFolder={handleAddQuestionToFolder}
  onRemoveQuestionFromFolder={handleRemoveQuestionFromFolder}
  themeStyles={themeStyles}
  isDarkMode={isDarkMode}
/>
*/